import type { Todo } from "@listto/powersync";
import {
  buildDataset,
  type ExportColumn,
  type ExportDataset,
  type ExportMetadata,
} from "@fleet/export";

/**
 * Canon adapter: maps a list of `todo` records into the generic
 * {@link ExportDataset}. This is the ONLY todo-specific piece — clones replace
 * it with an adapter for their own entity (see K1 propagation notes).
 *
 * Booleans stored as 0/1 integers in SQLite are normalized to "Yes"/"No";
 * date columns pass through as ISO strings (the consumer can localize).
 */

function yesNo(value: number | null | undefined): string {
  return value === 1 ? "Yes" : "No";
}

export const TODO_EXPORT_COLUMNS: ExportColumn<Todo>[] = [
  { key: "text", header: "Task", accessor: (t) => t.text ?? "" },
  { key: "completed", header: "Completed", accessor: (t) => yesNo(t.completed) },
  { key: "important", header: "Important", accessor: (t) => yesNo(t.important) },
  { key: "note", header: "Note", accessor: (t) => t.note ?? "" },
  { key: "due_date", header: "Due date", accessor: (t) => t.due_date ?? "" },
  { key: "my_day_date", header: "My day", accessor: (t) => t.my_day_date ?? "" },
  { key: "created_at", header: "Created", accessor: (t) => t.created_at ?? "" },
];

/**
 * Builds an {@link ExportDataset} from a list of todos, ready for
 * `datasetToCsv` / `datasetToHtml`.
 */
export function buildTodoDataset(
  todos: readonly Todo[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(TODO_EXPORT_COLUMNS, todos, {
    title: "Tasks",
    ...metadata,
  });
}
